<template>
  <div class="matterport-showcase">
    <a :href="url" class="teaser" target="_blank" @click.prevent="openModal">
      <div class="teaser-media">
        <picture class="responsive-image">
          <source
            v-for="(source, i) in sources"
            :key="i"
            :media="source.media"
            type="image/jpeg"
            :srcset="source.srcset"
          />
          <!-- eslint-disable-next-line vuejs-accessibility/alt-text -->
          <img width="800" height="450" :src="fallback" loading="lazy" />
        </picture>
      </div>
      <div class="button is-rounded">
        <span class="icon"><i class="mdi mdi-play is-size-2" /></span>
      </div>
      <span class="sr-only">Open matterport modal</span>
    </a>

    <PageModal :modal-id>
      <iframe
        title="matterport-showcase"
        class="h-screen w-screen"
        scrolling="no"
        :src="url"
        frameborder="0"
        allowfullscreen
        allow="xr-spatial-tracking"
      />
    </PageModal>
  </div>
</template>

<script lang="ts" setup>
import { BREAKPOINTS, Viewport } from '~/helpers/responsiveStyles'

const props = defineProps<{
  matterportId: string
}>()

const SIZES: Partial<Record<Viewport, number>> = {
  [Viewport.Mobile]: 375,
  [Viewport.Tablet]: 440,
  [Viewport.Desktop]: 650,
  [Viewport.Widescreen]: 716,
  [Viewport.FullHD]: 800,
}

const { openModal, modalId } = useModal('matterport')

const url = computed(
  () => `https://my.matterport.com/show/?m=${props.matterportId}&play=1`,
)

function getThumbnailUrl(width: number) {
  return `https://my.matterport.com/api/v1/player/models/${props.matterportId}/thumb?width=${width}&disable=upscale`
}

function getDataForViewport(viewport: Viewport) {
  const width = SIZES[viewport] as number
  return {
    media: BREAKPOINTS[viewport],
    srcset: [
      getThumbnailUrl(width) + ' 1x',
      getThumbnailUrl(width * 2) + ' 2x',
    ].join(', '),
  }
}

const sources = computed(() =>
  Object.keys(SIZES)
    .map((v) => getDataForViewport(v as Viewport))
    .reverse(),
)

const fallback = computed(() => getThumbnailUrl(800))
</script>

<style>
@reference '~/assets/css/tailwind.css';
.matterport-showcase {
  .image {
    @apply bg-scheme-main-ter;
  }

  .modal {
    iframe {
      @apply h-screen w-screen;
    }
  }

  .teaser .button {
    @apply absolute text-[2rem];
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
}
</style>
